import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Copy, Check, Clock, XCircle, MessageCircle, RefreshCw, LogOut, ShieldCheck, ImagePlus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth, nextRoute } from '../context/AuthContext';
import { getPaymentInfo, getPaymentStatus, confirmPayment, uploadProof, fileUrl, formatRupiahFull } from '../api';

const FEATURES = ['Checklist lengkap 8 kategori', 'Budget, pengeluaran & dana nikah', 'Manajemen tamu unlimited', 'Undangan digital + WhatsApp', 'Akses seumur hidup'];

export default function Bayar() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, refresh, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [payment, setPayment] = useState(null);
  const [form, setForm] = useState({ senderName: user?.name || '', senderBank: '', amount: '', phone: '' });
  const [proof, setProof] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const pickProof = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast({ title: 'Format harus JPG, PNG, atau WebP', variant: 'destructive' }); return; }
    if (f.size > 5 * 1024 * 1024) { toast({ title: 'Ukuran maksimal 5MB', variant: 'destructive' }); return; }
    setProof(f); setProofPreview(URL.createObjectURL(f));
  };
  const clearProof = () => { setProof(null); if (proofPreview) URL.revokeObjectURL(proofPreview); setProofPreview(null); };

  const check = async (silent = true) => {
    const s = await getPaymentStatus();
    setPayment(s.payment);
    if (s.is_paid) {
      const me = await refresh();
      toast({ title: 'Akun aktif! 🎉', description: 'Selamat, semua fitur sudah terbuka.' });
      navigate(nextRoute(me.user));
    } else if (!silent) toast({ title: 'Belum diverifikasi', description: 'Admin masih memeriksa pembayaranmu.' });
  };

  useEffect(() => {
    getPaymentInfo().then((i) => { setInfo(i); setForm(f => ({ ...f, amount: i.price })); });
    check();
    const t = setInterval(check, 15000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copyAcc = () => { navigator.clipboard?.writeText(info.bankAccount); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.senderName || !form.senderBank || !form.amount) { toast({ title: 'Lengkapi data konfirmasi', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      let proofFileId = null;
      if (proof) proofFileId = (await uploadProof(proof)).id;
      const p = await confirmPayment({ ...form, amount: Number(form.amount), proofFileId });
      setPayment(p); clearProof();
      toast({ title: 'Konfirmasi terkirim', description: 'Admin akan memverifikasi dalam 1x24 jam.' });
    } catch (err) { toast({ title: 'Gagal', description: err?.response?.data?.detail || 'Coba lagi', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const waAdmin = () => {
    const msg = encodeURIComponent(`Halo Admin Naik Pelaminan, saya ${user?.name} (${user?.email}) sudah transfer ${formatRupiahFull(info.price)} untuk ${info.package}. Berikut bukti transfernya:`);
    window.open(`https://wa.me/${info.adminWa}?text=${msg}`, '_blank');
  };

  if (!info) return <div className="min-h-screen bg-cream-100 flex items-center justify-center text-sage-500">Memuat...</div>;
  const pending = payment?.status === 'pending';
  const rejected = payment?.status === 'rejected';

  return (
    <div className="min-h-screen bg-cream-100 px-5 py-10" data-testid="bayar-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-terracotta-500 flex items-center justify-center"><Heart className="w-5 h-5 text-white" fill="white" /></div>
            <div className="font-serif text-xl text-sage-700">Naik Pelaminan</div>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-sm text-sage-500 hover:text-terracotta-600" data-testid="bayar-logout-button"><LogOut className="w-4 h-4" /> Keluar</button>
        </div>

        <div className="mb-8">
          <div className="text-terracotta-500 text-xs tracking-[0.3em] font-medium mb-2">SATU LANGKAH LAGI</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-sage-700">Aktifkan akunmu, {user?.name?.split(' ')[0]}</h1>
          <p className="text-sage-500 mt-2 max-w-xl">Bayar sekali, pakai selamanya. Setelah transfer, isi konfirmasi di bawah dan admin kami akan mengaktifkan akunmu.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-sage-700 text-white rounded-3xl p-7 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-terracotta-500/30" />
              <div className="relative">
                <div className="text-cream-100 text-xs tracking-widest mb-3">PAKET LENGKAP</div>
                <div className="flex items-end gap-2 mb-1">
                  <span className="font-serif text-5xl" data-testid="package-price">{formatRupiahFull(info.price)}</span>
                </div>
                <div className="text-cream-100/80 text-sm mb-6">sekali bayar · seumur hidup</div>
                <ul className="space-y-2.5">
                  {FEATURES.map((f, i) => <li key={i} className="flex items-center gap-2 text-sm text-cream-100"><Check className="w-4 h-4 text-terracotta-300" /> {f}</li>)}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 border border-cream-200">
              <div className="text-xs text-sage-500 tracking-widest mb-3">TRANSFER KE</div>
              <div className="font-semibold text-sage-700 text-lg">{info.bankName}</div>
              <div className="flex items-center justify-between mt-1">
                <div className="font-mono text-2xl text-terracotta-600 tracking-wider" data-testid="bank-account">{info.bankAccount}</div>
                <button onClick={copyAcc} className="w-9 h-9 rounded-full bg-cream-100 hover:bg-cream-200 flex items-center justify-center" data-testid="copy-account-button">
                  {copied ? <Check className="w-4 h-4 text-sage-600" /> : <Copy className="w-4 h-4 text-sage-600" />}
                </button>
              </div>
              <div className="text-sm text-sage-500 mt-1">a.n. {info.bankHolder}</div>
              <div className="mt-4 pt-4 border-t border-cream-100 text-sm text-sage-600">Nominal: <span className="font-semibold text-sage-700">{formatRupiahFull(info.price)}</span></div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {pending ? (
              <div className="bg-white rounded-3xl p-8 border border-cream-200 h-full flex flex-col" data-testid="payment-pending-card">
                <div className="w-14 h-14 rounded-2xl bg-terracotta-50 flex items-center justify-center mb-5"><Clock className="w-7 h-7 text-terracotta-500" /></div>
                <h2 className="font-serif text-3xl text-sage-700 mb-2">Menunggu verifikasi</h2>
                <p className="text-sage-500 text-sm mb-6">Konfirmasi dari <b>{payment.senderName}</b> ({payment.senderBank}) sebesar {formatRupiahFull(payment.amount)} sudah kami terima. Admin biasanya memverifikasi dalam 1x24 jam. Halaman ini akan otomatis terbuka saat akun aktif.</p>
                {payment.proofFileId && (
                  <a href={fileUrl(payment.proofFileId)} target="_blank" rel="noreferrer" className="block mb-6" data-testid="pending-proof-preview">
                    <img src={fileUrl(payment.proofFileId)} alt="Bukti transfer" className="h-32 rounded-2xl object-cover border border-cream-200" />
                  </a>
                )}
                <div className="bg-cream-50 rounded-2xl p-4 text-sm text-sage-600 mb-6 flex gap-3"><ShieldCheck className="w-5 h-5 text-sage-500 flex-shrink-0" /> {payment.proofFileId ? 'Bukti transfer sudah terlampir. Kamu juga bisa menghubungi admin via WhatsApp.' : 'Percepat prosesnya: kirim bukti transfer ke WhatsApp admin.'}</div>
                <div className="flex flex-col sm:flex-row gap-3 mt-auto">
                  <Button onClick={waAdmin} className="flex-1 h-11 bg-sage-600 hover:bg-sage-700 text-white rounded-full" data-testid="wa-admin-button"><MessageCircle className="w-4 h-4 mr-2" /> Kirim bukti via WA</Button>
                  <Button onClick={() => check(false)} variant="outline" className="flex-1 h-11 rounded-full border-cream-200" data-testid="check-status-button"><RefreshCw className="w-4 h-4 mr-2" /> Cek status</Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl p-8 border border-cream-200" data-testid="payment-confirm-form">
                {rejected && (
                  <div className="bg-red-50 border border-red-100 rounded-2xl p-4 mb-6 flex gap-3 text-sm" data-testid="payment-rejected-alert">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <div><div className="font-medium text-red-600">Konfirmasi sebelumnya ditolak</div><div className="text-red-500/80 mt-0.5">{payment.reason || 'Pembayaran tidak ditemukan. Cek kembali data transfermu dan kirim ulang.'}</div></div>
                  </div>
                )}
                <h2 className="font-serif text-3xl text-sage-700 mb-1">Sudah transfer?</h2>
                <p className="text-sage-500 text-sm mb-6">Isi data di bawah agar admin bisa mencocokkan mutasi rekening.</p>
                <div className="space-y-4">
                  <div><Label className="text-sage-600 text-sm">Nama pengirim (sesuai rekening)</Label><Input value={form.senderName} onChange={(e) => setForm({ ...form, senderName: e.target.value })} className="mt-1.5 h-11 bg-cream-50 border-cream-200 rounded-xl" data-testid="sender-name-input" /></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-sage-600 text-sm">Bank pengirim</Label><Input value={form.senderBank} onChange={(e) => setForm({ ...form, senderBank: e.target.value })} placeholder="mis. BCA, Mandiri, GoPay" className="mt-1.5 h-11 bg-cream-50 border-cream-200 rounded-xl" data-testid="sender-bank-input" /></div>
                    <div><Label className="text-sage-600 text-sm">Nominal transfer (Rp)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-11 bg-cream-50 border-cream-200 rounded-xl" data-testid="amount-input" /></div>
                  </div>
                  <div><Label className="text-sage-600 text-sm">Nomor WhatsApp kamu</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xxxxxxxxxx" className="mt-1.5 h-11 bg-cream-50 border-cream-200 rounded-xl" data-testid="phone-input" /><p className="text-xs text-sage-400 mt-1">Kami kabari lewat WA saat akun sudah aktif.</p></div>
                  <div>
                    <Label className="text-sage-600 text-sm">Bukti transfer <span className="text-sage-400">(opsional, maks 5MB)</span></Label>
                    {proofPreview ? (
                      <div className="relative mt-1.5 inline-block" data-testid="proof-preview">
                        <img src={proofPreview} alt="Bukti transfer" className="h-32 rounded-2xl object-cover border border-cream-200" />
                        <button type="button" onClick={clearProof} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-cream-200 flex items-center justify-center shadow-sm" data-testid="remove-proof-button"><X className="w-3.5 h-3.5 text-sage-600" /></button>
                      </div>
                    ) : (
                      <label className="mt-1.5 flex items-center gap-3 h-14 px-4 rounded-xl border border-dashed border-cream-300 bg-cream-50 hover:bg-cream-100 cursor-pointer text-sm text-sage-600" data-testid="proof-dropzone">
                        <ImagePlus className="w-5 h-5 text-terracotta-500" /> Pilih screenshot bukti transfer
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickProof} className="hidden" data-testid="proof-file-input" />
                      </label>
                    )}
                  </div>
                </div>
                <Button type="submit" disabled={busy} className="w-full h-12 mt-6 bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-full text-base" data-testid="confirm-payment-button">{busy ? (proof ? 'Mengunggah bukti...' : 'Mengirim...') : 'Saya sudah transfer'}</Button>
                <p className="text-xs text-sage-400 text-center mt-4">Butuh bantuan? <button type="button" onClick={waAdmin} className="text-terracotta-500 underline">Chat admin via WhatsApp</button></p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
